import { Layer, Source, useMap } from '@vis.gl/react-maplibre';
import type { MapLayerEventType } from 'maplibre-gl';
import { useCallback, useState } from 'react';

import { BEFORE_ROUTE_ID } from '@/components/map/RouteLayers';
import { useMapLayerOnEvent } from '@/hooks/useMapLayerOnEvent';
import { LAYERS } from '@/modules/Layers';
import { useMapStore } from '@/stores/useMapStore';
import type { MapState } from '@/types/MapState';

type AvailableLayers = keyof Pick<
  MapState['layersControl'],
  'marathon2025' | 'iaa2025' | 'priorityNetworkFireDepartment' | 'tumAudimaxEvacuation' | 'studyArea'
>;
interface JsonLayerParams {
  layer: AvailableLayers;
}

export function JsonLayer({ layer: key }: JsonLayerParams) {
  const { source, layer } = LAYERS[key];

  const [hoveredStateId, setHoveredStateId] = useState<number | string>();
  const selected = useMapStore((state) => state.selectedFeature);
  const { setSelectedFeature: setSelected } = useMapStore((state) => state.actions);
  const map = useMap();

  const handleMouseMove = useCallback(
    (event: MapLayerEventType['mousemove']) => {
      if (selected || !event.features) return;
      const id = event.features[0]?.id;
      if (map.current) map.current.getCanvas().style.cursor = 'pointer';
      if (hoveredStateId) {
        map.current?.setFeatureState({ source: key, id: hoveredStateId }, { hover: false });
      }
      setHoveredStateId(id);
      map.current?.setFeatureState({ source: key, id }, { hover: true });
    },
    [hoveredStateId, map, selected, key],
  );
  const handleMouseLeave = useCallback(() => {
    if (map.current) map.current.getCanvas().style.cursor = '';
    if (selected) return;
    if (hoveredStateId !== undefined) {
      map.current?.setFeatureState({ source: key, id: hoveredStateId }, { hover: false });
    }
    setHoveredStateId(undefined);
  }, [hoveredStateId, map, selected, key]);

  const handleClick = useCallback(
    (event: MapLayerEventType['click']) => {
      if (!event.features) return;
      let content = '';
      if (event.features[0]) {
        const { properties } = event.features[0];
        if (key === 'priorityNetworkFireDepartment') content = 'NETZFUNK_1' in properties && properties.NETZFUNK_1;
        if (key === 'tumAudimaxEvacuation') content = 'distance' in properties ? `${properties.distance} m` : '';
        setSelected({
          title: LAYERS[key].label,
          content,
          coordinates: [event.lngLat.lng, event.lngLat.lat],
        });
      }
    },
    [key, setSelected],
  );

  useMapLayerOnEvent({ type: 'mousemove', layer: key, listener: handleMouseMove });
  useMapLayerOnEvent({ type: 'mouseleave', layer: key, listener: handleMouseLeave });
  useMapLayerOnEvent({ type: 'click', layer: key, listener: handleClick });

  if (!source || !layer) return null;

  return (
    <Source id={key} {...source}>
      <Layer beforeId={BEFORE_ROUTE_ID} {...layer} />
    </Source>
  );
}
